import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTierLabel, getTierColor, getTierIcon } from '../utils/tiers';
import LoginModal from './LoginModal';
import AccountDropdown from './AccountDropdown';
import { trackEvent } from '../utils/analytics';

// Wallet icon
const WalletIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a1 1 0 0 0 0 4h4v-4h-4z" />
  </svg>
);

const Header: React.FC = () => {
  const { user, isAuthenticated, isLoading, showLoginModal, setShowLoginModal, openLogin, clearPendingLoginRedirect } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);

  const truncateAddress = (address: string): string => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <>
      <header className="border-b border-gray-800 bg-crypto-gray/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 text-white no-underline">
              <svg width="28" height="28" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="15" y1="15" x2="15" y2="3"  stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="15" x2="26" y2="9"  stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="15" x2="26" y2="21" stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="15" x2="15" y2="27" stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="15" x2="4"  y2="21" stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="15" x2="4"  y2="9"  stroke="#5b6ef5" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="15" y1="3"  x2="26" y2="9"  stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <line x1="26" y1="9"  x2="26" y2="21" stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <line x1="26" y1="21" x2="15" y2="27" stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <line x1="15" y1="27" x2="4"  y2="21" stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <line x1="4"  y1="21" x2="4"  y2="9"  stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <line x1="4"  y1="9"  x2="15" y2="3"  stroke="#5b6ef5" strokeWidth="1"   strokeLinecap="round" opacity="0.45"/>
                <circle cx="15" cy="3"  r="2.2" fill="#5b6ef5"/>
                <circle cx="26" cy="9"  r="2.2" fill="#5b6ef5"/>
                <circle cx="26" cy="21" r="2.2" fill="#5b6ef5"/>
                <circle cx="15" cy="27" r="2.2" fill="#5b6ef5"/>
                <circle cx="4"  cy="21" r="2.2" fill="#5b6ef5"/>
                <circle cx="4"  cy="9"  r="2.2" fill="#5b6ef5"/>
                <circle cx="15" cy="15" r="3.5" fill="#8b9ef8"/>
              </svg>
              <span className="text-xl font-bold">deepnets.ai</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              <Link
                to="/trending"
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              >
                <i className="fas fa-fire text-orange-400 text-xs"></i>
                Trending
              </Link>
              <Link
                to="/figures"
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              >
                <i className="fas fa-user-secret text-purple-400 text-xs"></i>
                Figures
              </Link>
              <Link
                to="/agents"
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              >
                <i className="fas fa-robot text-crypto-accent text-xs"></i>
                Agents
              </Link>
              <Link
                to="/whitepaper"
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              >
                <i className="fas fa-file-alt text-gray-500 text-xs"></i>
                $DNET Whitepaper
              </Link>
            </nav>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex items-center justify-center w-9 h-9 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
              aria-label="Menu"
            >
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>

            {/* Auth section */}
            <div className="flex items-center">
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-gray-600 border-t-crypto-accent rounded-full animate-spin" />
              ) : isAuthenticated && user ? (
                <div className="relative">
                  <button
                    ref={accountTriggerRef}
                    onClick={() => setAccountOpen(o => !o)}
                    aria-haspopup="menu"
                    aria-expanded={accountOpen}
                    className="flex items-center gap-0 bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700 rounded-lg transition-colors overflow-hidden"
                  >
                    {/* Wallet icon + display name or truncated address */}
                    <div className="flex items-center gap-1.5 px-3 py-2 border-r border-gray-700">
                      <WalletIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-300 text-sm font-mono hidden sm:inline">
                        {user.username || user.email?.split('@')[0] || (user.walletAddress ? truncateAddress(user.walletAddress) : 'Profile')}
                      </span>
                    </div>

                    {/* Tier badge */}
                    <div className="flex items-center gap-1.5 px-3 py-2">
                      {user.tier && user.tier !== 'free' && (
                        <span className={`${getTierColor(user.tier)} text-xs font-semibold`}>
                          {getTierIcon(user.tier)} {getTierLabel(user.tier)}
                        </span>
                      )}
                      {user.tier === 'free' && (
                        <span className="text-gray-500 text-xs">Free</span>
                      )}
                      <i className={`fas fa-chevron-down text-gray-500 text-[10px] ml-1 transition-transform ${accountOpen ? 'rotate-180' : ''}`}></i>
                    </div>
                  </button>
                  <AccountDropdown
                    open={accountOpen}
                    onClose={() => setAccountOpen(false)}
                    triggerRef={accountTriggerRef}
                  />
                </div>
              ) : (
                <button
                  onClick={() => {
                    openLogin('header');
                  }}
                  className="flex items-center gap-2 bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700 rounded-lg px-4 py-2 transition-colors"
                >
                  <WalletIcon className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-300 text-sm font-medium">Login</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-gray-800 mt-3 pt-3 pb-1 flex flex-col space-y-1">
            <Link
              to="/trending"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <i className="fas fa-fire text-orange-400 text-xs w-4 text-center"></i>
              Trending
            </Link>
            <Link
              to="/figures"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <i className="fas fa-user-secret text-purple-400 text-xs w-4 text-center"></i>
              Figures
            </Link>
            <Link
              to="/agents"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <i className="fas fa-robot text-crypto-accent text-xs w-4 text-center"></i>
              Agents
            </Link>
            <Link
              to="/whitepaper"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <i className="fas fa-file-alt text-gray-500 text-xs w-4 text-center"></i>
              Whitepaper
            </Link>
            <a
              href="https://x.com/i/chat/group_join/g2050256478343213064/QYTP6iN6Q6"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors"
            >
              <i className="fas fa-comments text-blue-400 text-xs w-4 text-center"></i>
              XChat group ↗
            </a>
          </nav>
        )}
      </header>

      {/* Login modal */}
      <LoginModal isOpen={showLoginModal} onClose={() => {
        // If the modal closes without a successful login, drop any
        // pending post-login redirect so it doesn't fire later when the
        // user logs in for an unrelated reason.
        if (!isAuthenticated) {
          trackEvent('login_modal_dismissed');
          clearPendingLoginRedirect();
        }
        setShowLoginModal(false);
      }} />
    </>
  );
};

export default Header;
